import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Play, CheckCircle2, Gift } from "lucide-react";

export default function Education() {
  const { refreshUser } = useAuth();
  const [videos, setVideos] = useState([]);
  const [progress, setProgress] = useState(null);
  const [active, setActive] = useState(null);
  const [watchTime, setWatchTime] = useState(0);
  const [quiz, setQuiz] = useState(null);
  // Pending bonus-video tokens (skipped from game-over)
  const [pendingBonuses, setPendingBonuses] = useState([]);
  const [activeBonus, setActiveBonus] = useState(null);
  const [bonusWatch, setBonusWatch] = useState(0);
  const [bonusClaiming, setBonusClaiming] = useState(false);

  const load = useCallback(async () => {
    const [v, p, b] = await Promise.all([
      api.get("/education/videos"),
      api.get("/education/progress"),
      api.get("/education/bonus-pending"),
    ]);
    setVideos(v.data.videos || []);
    setProgress(p.data);
    setPendingBonuses(b.data.pending || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!active) return;
    setWatchTime(0);
    const i = setInterval(() => setWatchTime((w) => w + 1), 1000);
    return () => clearInterval(i);
  }, [active]);

  useEffect(() => {
    if (!activeBonus) return;
    setBonusWatch(0);
    const i = setInterval(() => setBonusWatch((w) => w + 1), 1000);
    return () => clearInterval(i);
  }, [activeBonus]);

  const claim = async (v) => {
    try {
      const { data } = await api.post("/education/complete", {
        video_id: v.video_id,
        watch_time: watchTime,
      });
      toast.success(`+${data.bc_earned} BC — nice watch!`);
      setQuiz(v);
      await refreshUser();
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const answerQuiz = async (idx) => {
    try {
      const { data } = await api.post("/education/quiz", {
        video_id: quiz.video_id,
        answer_index: idx,
      });
      toast[data.correct ? "success" : "info"](
        `${data.correct ? "✔ correct!" : "× wrong"} +${data.bc_earned} BC`,
      );
      setQuiz(null);
      setActive(null);
      await refreshUser();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const claimBonus = async () => {
    if (!activeBonus) return;
    setBonusClaiming(true);
    try {
      const { data } = await api.post("/education/bonus-claim", {
        token: activeBonus.token,
        watch_time: bonusWatch,
      });
      toast.success(`+${data.bc_earned} BC — bonus claimed!`);
      await refreshUser();
      setActiveBonus(null);
      setBonusWatch(0);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBonusClaiming(false);
    }
  };

  const formatExpiry = (iso) => {
    const d = new Date(iso);
    const mins = Math.max(0, Math.floor((d - new Date()) / 60000));
    return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  return (
    <Layout>
      <div className="px-4 py-5 flex flex-col gap-4" data-testid="education-page">
        <h1 className="font-pixel text-[14px] text-glow-green">▶ LEARN</h1>
        {progress && (
          <div className="cb-card p-3 flex items-center justify-between">
            <span className="text-[11px] text-[#8b95a9]">PROGRESS</span>
            <span
              className="font-mono text-[13px] text-[#2ecc71]"
              data-testid="edu-progress"
            >
              {progress.completed_videos}/{progress.total_videos} (
              {progress.progress_pct}%)
            </span>
          </div>
        )}

        {/* Pending bonus section */}
        {pendingBonuses.length > 0 && !activeBonus && !active && (
          <section
            className="flex flex-col gap-2"
            data-testid="bonus-pending-section"
          >
            <h2 className="font-pixel text-[10px] text-[#f7931a] text-glow-gold flex items-center gap-2">
              <Gift size={12} /> BONUS VIDEOS PENDING ({pendingBonuses.length})
            </h2>
            {pendingBonuses.map((b) => (
              <div
                key={b.token}
                className="p-3 rounded-[8px] border-2 border-[rgba(247,147,26,0.55)] bg-[rgba(247,147,26,0.08)] flex gap-3 items-center"
                data-testid={`bonus-pending-${b.token}`}
              >
                <div className="w-14 h-14 rounded-[6px] bg-[rgba(247,147,26,0.15)] flex items-center justify-center">
                  <Gift size={22} className="text-[#f7931a]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-white truncate">
                    {b.title}
                  </div>
                  <div className="text-[9px] text-[#8b95a9] mt-1">
                    After {b.games_played_total} games · +{b.reward} BC · expires in{" "}
                    {formatExpiry(b.expires_at)}
                  </div>
                </div>
                <button
                  onClick={() => setActiveBonus(b)}
                  data-testid={`bonus-watch-${b.token}`}
                  className="cb-neon-btn orange text-[9px] py-2 px-3"
                >
                  Watch
                </button>
              </div>
            ))}
          </section>
        )}

        {/* Active bonus viewer */}
        {activeBonus && (
          <div
            className="cb-card p-4 flex flex-col gap-3 border-[rgba(247,147,26,0.55)]"
            data-testid="bonus-active"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-pixel text-[11px] text-[#f7931a] text-glow-gold flex items-center gap-2">
                <Gift size={12} /> BONUS · +{activeBonus.reward} BC
              </h2>
              <button
                onClick={() => setActiveBonus(null)}
                data-testid="bonus-close-btn"
                className="text-white/50 text-[10px]"
              >
                ✕ close
              </button>
            </div>
            <p className="text-[10px] text-white/80">{activeBonus.title}</p>
            <div className="aspect-video w-full rounded-[8px] overflow-hidden border border-[rgba(247,147,26,0.5)]">
              <iframe
                title={activeBonus.title}
                src={`https://www.youtube.com/embed/${activeBonus.youtube_id}?autoplay=1&rel=0`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="bonus-iframe"
              />
            </div>
            <div className="flex items-center justify-between text-[10px] text-[#8b95a9] font-mono">
              <span>
                {bonusWatch}s / min {activeBonus.min_watch_seconds}s
              </span>
              <span>expires in {formatExpiry(activeBonus.expires_at)}</span>
            </div>
            <button
              onClick={claimBonus}
              disabled={
                bonusClaiming || bonusWatch < activeBonus.min_watch_seconds
              }
              data-testid="bonus-active-claim-btn"
              className="cb-neon-btn orange"
            >
              {bonusClaiming ? "..." : `Claim +${activeBonus.reward} BC`}
            </button>
          </div>
        )}

        {active ? (
          <div className="cb-card p-4 flex flex-col gap-3">
            <div className="aspect-video w-full rounded-[8px] overflow-hidden border border-[rgba(46,117,182,0.5)]">
              <iframe
                title={active.title}
                src={`https://www.youtube.com/embed/${active.youtube_id}?autoplay=1`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="edu-video-iframe"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-[#8b95a9]">
                Watch time · {watchTime}s / min{" "}
                {Math.max(30, Math.round(active.duration_seconds * 0.6))}s
              </span>
              <span className="font-pixel text-[10px] text-[#f7931a]">
                +{active.reward} BC
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setActive(null)}
                data-testid="edu-close-btn"
                className="cb-neon-btn blue flex-1"
              >
                Close
              </button>
              <button
                onClick={() => claim(active)}
                disabled={
                  watchTime <
                  Math.max(30, Math.round(active.duration_seconds * 0.6))
                }
                data-testid="edu-claim-btn"
                className="cb-neon-btn flex-1"
              >
                Claim BC
              </button>
            </div>
            {quiz && (
              <div className="mt-3 cb-card p-3">
                <h3 className="font-pixel text-[10px] text-[#2ecc71] mb-2">
                  QUIZ
                </h3>
                <p className="text-[12px] text-white mb-3">
                  {quiz.quiz.question}
                </p>
                <div className="flex flex-col gap-2">
                  {quiz.quiz.options.map((o, i) => (
                    <button
                      key={`${quiz.video_id}-${o}`}
                      onClick={() => answerQuiz(i)}
                      data-testid={`quiz-option-${i}`}
                      className="cb-neon-btn blue text-[10px]"
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          videos.map((v) => (
            <div
              key={v.video_id}
              className="cb-card p-4 flex gap-3 items-center"
              data-testid={`edu-card-${v.video_id}`}
            >
              <div className="w-20 h-14 bg-[rgba(46,117,182,0.2)] rounded-[6px] flex items-center justify-center">
                <Play size={22} className="text-[#2ecc71]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] text-white truncate">
                  {v.title}
                </div>
                <div className="text-[9px] text-[#8b95a9] mt-1">
                  {Math.round(v.duration_seconds / 60)} min · +{v.reward} BC
                </div>
                {v.completed_today && (
                  <div className="text-[9px] text-[#2ecc71] mt-1">
                    <CheckCircle2 size={9} className="inline" /> Done today
                  </div>
                )}
              </div>
              <button
                onClick={() => setActive(v)}
                data-testid={`edu-watch-${v.video_id}`}
                className="cb-pill"
              >
                Watch
              </button>
            </div>
          ))
        )}
      </div>
    </Layout>
  );
}
